import React, { useEffect, useState } from "react";
import { adminApi } from "@/admin/AdminAuthContext";
import { toast } from "sonner";
import {
  Shield, KeyRound, Activity, Database, Loader2, Check, AlertTriangle,
  ExternalLink, Clock, RefreshCw,
} from "lucide-react";

// --- Helpers ---------------------------------------------------------------
const formatRelative = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short", timeZone: "Indian/Reunion" });
  } catch { return iso; }
};

const ACTION_LABELS = {
  "admin.login.success": "Connexion réussie",
  "admin.login.failed": "Échec de connexion",
  "admin.login.lockout": "Compte verrouillé (brute-force)",
  "admin.logout": "Déconnexion",
  "admin.password.change.success": "Mot de passe modifié",
  "admin.password.change.failed": "Échec changement mot de passe",
  "admin.backup.manual": "Sauvegarde manuelle DB",
};

// ---------------------------------------------------------------------------
// Health Status
// ---------------------------------------------------------------------------
function StatusPill({ ok, label, hint }) {
  return (
    <div className={`rounded-2xl border p-4 ${ok ? "border-emerald-500/25 bg-emerald-500/[0.04]" : "border-amber-500/30 bg-amber-500/[0.05]"}`}>
      <div className="flex items-center gap-2">
        {ok
          ? <div className="w-7 h-7 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center"><Check size={14} className="text-emerald-300" /></div>
          : <div className="w-7 h-7 rounded-xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center"><AlertTriangle size={14} className="text-amber-300" /></div>}
        <div className="text-sm font-medium text-white/90">{label}</div>
      </div>
      {hint && <div className="mt-2 text-[11px] text-white/55 leading-relaxed">{hint}</div>}
    </div>
  );
}

function HealthBlock({ health, onRefresh, refreshing }) {
  if (!health) {
    return <div className="text-sm text-white/50">Chargement…</div>;
  }
  return (
    <div data-testid="health-block">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-white/55">
          Dernier check&nbsp;: <span className="text-white/80">{formatRelative(health.checked_at)}</span>
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          data-testid="health-refresh"
          className="inline-flex items-center gap-1.5 text-xs text-white/65 hover:text-white border border-white/10 rounded-full px-3 py-1.5 disabled:opacity-50"
        >
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> Actualiser
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <StatusPill ok={health.db_ping} label="MongoDB ping" hint={health.db_ping ? "Base accessible" : "Base injoignable — investiguer"} />
        <StatusPill
          ok={health.stripe_webhook_secret_configured}
          label="Webhook Stripe signé"
          hint={
            health.stripe_webhook_secret_configured
              ? "STRIPE_WEBHOOK_SECRET configuré — signature vérifiée"
              : "⚠ STRIPE_WEBHOOK_SECRET absent — toute requête anonyme est acceptée"
          }
        />
        <StatusPill
          ok={health.stripe_live_mode}
          label="Stripe en mode LIVE"
          hint={health.stripe_live_mode ? "Paiements réels actifs (sk_live_*)" : "Mode test (sk_test_*) — pas de vrais paiements"}
        />
        <StatusPill ok={health.resend_configured} label="Resend (emails)" hint={health.resend_configured ? "API key Resend configurée" : "Pas d'envoi d'emails possible"} />
        <StatusPill ok={health.google_places_configured} label="Google Places API" hint={health.google_places_configured ? "Enrichissement prospects actif" : "Clé manquante — enrichissement bloqué"} />
        <StatusPill
          ok={!!health.last_db_backup && health.last_db_backup.status === "ok"}
          label="Dernier backup MongoDB"
          hint={
            health.last_db_backup
              ? `${formatRelative(health.last_db_backup.created_at)} · ${(health.last_db_backup.size_bytes / 1024).toFixed(1)} KB`
              : "Aucun backup enregistré — lancez-en un manuellement"
          }
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Change password
// ---------------------------------------------------------------------------
function PasswordChangeBlock() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [nw2, setNw2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 10) { toast.error("10 caractères minimum."); return; }
    if (nw !== nw2) { toast.error("Les deux nouveaux mots de passe diffèrent."); return; }
    setBusy(true);
    try {
      await adminApi.post("/admin/auth/change-password", {
        current_password: cur, new_password: nw,
      });
      toast.success("Mot de passe modifié.");
      setCur(""); setNw(""); setNw2("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur.");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3 max-w-md" data-testid="password-change-form">
      <input
        type="password" autoComplete="current-password"
        placeholder="Mot de passe actuel"
        value={cur} onChange={(e) => setCur(e.target.value)}
        data-testid="password-current"
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/35 focus:border-[#E52431]/60 outline-none"
      />
      <input
        type="password" autoComplete="new-password"
        placeholder="Nouveau mot de passe (10 caractères min.)"
        value={nw} onChange={(e) => setNw(e.target.value)}
        data-testid="password-new"
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/35 focus:border-[#E52431]/60 outline-none"
      />
      <input
        type="password" autoComplete="new-password"
        placeholder="Confirmer le nouveau mot de passe"
        value={nw2} onChange={(e) => setNw2(e.target.value)}
        data-testid="password-new-confirm"
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/35 focus:border-[#E52431]/60 outline-none"
      />
      <button
        type="submit"
        disabled={busy || !cur || !nw || !nw2}
        data-testid="password-change-submit"
        className="inline-flex items-center gap-2 rounded-full gradient-bg text-white font-semibold px-6 py-3 text-xs uppercase tracking-[0.15em] hover:shadow-[0_0_30px_rgba(229,36,49,0.4)] transition disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound size={14} />}
        {busy ? "Modification…" : "Modifier le mot de passe"}
      </button>
      <p className="text-[11px] text-white/45 leading-relaxed">
        Astuce&nbsp;: utilisez 16+ caractères avec un mix maj/min/chiffres/symboles. Un gestionnaire de mots de passe (1Password, Bitwarden) est fortement recommandé.
      </p>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------
function AuditLogBlock() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/audit/?limit=50&since_days=30");
      setItems(data || []);
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="audit-log-block">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-white/55">
          Activité des 30 derniers jours — {items.length} entrées
        </div>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 text-xs text-white/65 hover:text-white border border-white/10 rounded-full px-3 py-1.5 disabled:opacity-50">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Actualiser
        </button>
      </div>
      {loading ? (
        <div className="text-sm text-white/50 py-6 text-center">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-white/50 py-6 text-center">Aucune entrée.</div>
      ) : (
        <div className="rounded-2xl border border-white/8 overflow-hidden divide-y divide-white/5 max-h-[500px] overflow-y-auto">
          {items.map((it, i) => (
            <div key={i} className="px-4 py-3 flex items-start gap-3 text-xs hover:bg-white/[0.015]" data-testid={`audit-entry-${i}`}>
              <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${it.success ? "bg-emerald-400" : "bg-[#E52431]"}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white/90 font-medium">{ACTION_LABELS[it.action] || it.action}</span>
                  <span className="text-white/45">·</span>
                  <span className="text-white/55">{it.admin_email || "—"}</span>
                </div>
                <div className="text-white/40 mt-0.5 flex items-center gap-2">
                  <Clock size={10} /> {formatRelative(it.created_at)}
                  {it.ip && <><span className="text-white/25">·</span><span className="font-mono">{it.ip}</span></>}
                </div>
                {it.details && Object.keys(it.details).length > 0 && (
                  <div className="mt-1 text-white/40 font-mono text-[10px] truncate">{JSON.stringify(it.details)}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function AdminSecurity() {
  const [health, setHealth] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [backupBusy, setBackupBusy] = useState(false);

  const loadHealth = async () => {
    setRefreshing(true);
    try {
      const { data } = await adminApi.get("/admin/health");
      setHealth(data);
    } catch (e) {
      toast.error("Erreur de chargement du health endpoint");
    }
    setRefreshing(false);
  };

  useEffect(() => { loadHealth(); }, []);

  const onManualBackup = async () => {
    setBackupBusy(true);
    try {
      const { data } = await adminApi.post("/admin/backup/run");
      if (data.status === "ok") {
        toast.success(`Backup OK — ${(data.size_bytes / 1024).toFixed(1)} KB`);
      } else {
        toast.error(`Backup ${data.status} — ${data.reason || "?"}`);
      }
      await loadHealth();
    } catch (err) {
      toast.error("Erreur backup.");
    } finally { setBackupBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-security-page">
      <header>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#E52431]/25 to-[#FF6A75]/10 border border-[#E52431]/35 flex items-center justify-center">
            <Shield size={22} className="text-[#FFB4BB]" />
          </div>
          <div>
            <h1 className="text-2xl font-light tracking-tight" data-testid="admin-security-title">Sécurité & Monitoring</h1>
            <p className="text-sm text-white/55 mt-0.5">Mot de passe, santé système, audit log et sauvegardes.</p>
          </div>
        </div>
      </header>

      {/* HEALTH STATUS */}
      <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
        <div className="flex items-center gap-2 mb-5">
          <Activity size={16} className="text-emerald-300" />
          <h2 className="text-base font-medium">État du système</h2>
        </div>
        <HealthBlock health={health} onRefresh={loadHealth} refreshing={refreshing} />
      </section>

      {/* CHANGE PASSWORD */}
      <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
        <div className="flex items-center gap-2 mb-5">
          <KeyRound size={16} className="text-[#FFB4BB]" />
          <h2 className="text-base font-medium">Changer le mot de passe</h2>
        </div>
        <PasswordChangeBlock />
      </section>

      {/* BACKUP MANUEL */}
      <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
        <div className="flex items-center gap-2 mb-3">
          <Database size={16} className="text-cyan-300" />
          <h2 className="text-base font-medium">Sauvegarde MongoDB</h2>
        </div>
        <p className="text-sm text-white/65 leading-relaxed max-w-2xl">
          Une sauvegarde complète est exécutée automatiquement chaque nuit à <strong className="text-white">03:00 (heure Réunion)</strong>. Les 14 dernières archives sont conservées. Vous pouvez en déclencher une manuellement ci-dessous.
        </p>
        <button
          onClick={onManualBackup}
          disabled={backupBusy}
          data-testid="manual-backup-btn"
          className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-white px-5 py-2.5 text-xs uppercase tracking-[0.12em] font-bold transition disabled:opacity-60"
        >
          {backupBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database size={14} />}
          {backupBusy ? "Sauvegarde…" : "Lancer une sauvegarde maintenant"}
        </button>
      </section>

      {/* AUDIT LOG */}
      <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
        <div className="flex items-center gap-2 mb-5">
          <ExternalLink size={16} className="text-amber-300" />
          <h2 className="text-base font-medium">Audit log</h2>
        </div>
        <AuditLogBlock />
      </section>
    </div>
  );
}
